// P3-022: Battle HUD layout per GDD section 4.5.8
//
// Layout structure:
//   Top: ProjectStatus (project name, turn counter, HP bar)
//   Upper-center: enemy area (handled by Phaser EnemySprite in BattleScene)
//   Center: board area (BoardView with Phaser canvas)
//   Bottom: AgentTeamBar (5 agent slots)
//   Overlays: ComboDisplay, HardwareOverlay

import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProjectStore } from '@/shared/stores/projectStore';
import { useBattleStore } from '../stores/battleStore';
import * as BattlePhase from '../systems/BattlePhase';
import { ProjectStatus } from './ProjectStatus';
import { BoardView } from './BoardView';
import { AgentTeamBar } from './AgentTeamBar';
import { ComboDisplay } from './ComboDisplay';
import { HardwareOverlay } from './HardwareOverlay';
import './phase3.css';

interface BattleLayoutProps {
  /** Project name to display in the status bar */
  projectName?: string;
  /** When embedded in hub, projectId is passed as prop instead of URL params */
  embeddedProjectId?: string;
  /** Called when battle is complete (embedded mode) */
  onComplete?: () => void;
  /** Called to exit battle early (embedded mode) */
  onExit?: () => void;
}

export function BattleLayout({ projectName, embeddedProjectId, onComplete }: BattleLayoutProps) {
  const { projectId: routeProjectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const projectId = embeddedProjectId ?? routeProjectId;
  const isEmbedded = !!embeddedProjectId;
  const project = useProjectStore((s) => (projectId ? s.projects[projectId] : undefined));
  const result = useBattleStore((s) => s.result);
  const enemy = useBattleStore((s) => s.enemy);
  const initialized = useRef(false);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [battleResultData, setBattleResultData] = useState<{
    outcome: 'VICTORY' | 'DEFEAT';
    turnsUsed: number;
    turnsTotal: number;
    totalDamage: number;
    maxCombo: number;
  } | null>(null);
  const layoutRef = useRef<HTMLDivElement>(null);

  // When embedded, raise #phaser-container above the hub background and
  // raise #react-ui above the canvas so the React HUD stays on top.
  useEffect(() => {
    if (!isEmbedded) return;
    const phaserContainer = document.getElementById('phaser-container');
    const reactUi = document.getElementById('react-ui');
    const hubBg = document.querySelector('.main-hub__background') as HTMLElement | null;
    const mainHub = document.querySelector('.main-hub') as HTMLElement | null;
    if (!phaserContainer || !layoutRef.current) return;

    const savedPhaserCss = phaserContainer.style.cssText;
    const savedReactUiZIndex = reactUi?.style.zIndex ?? '';
    const savedHubBgClip = hubBg?.style.clipPath ?? '';
    const savedMainHubBg = mainHub?.style.background ?? '';

    // Raise react-ui above the canvas so battle HUD stays visible
    if (reactUi) reactUi.style.zIndex = '30';
    // Make main-hub solid bg transparent so canvas shows in panel area
    if (mainHub) mainHub.style.background = 'transparent';

    const sync = () => {
      if (!layoutRef.current) return;
      const rect = layoutRef.current.getBoundingClientRect();
      phaserContainer.style.position = 'fixed';
      phaserContainer.style.top = `${Math.round(rect.top)}px`;
      phaserContainer.style.left = `${Math.round(rect.left)}px`;
      phaserContainer.style.width = `${Math.round(rect.width)}px`;
      phaserContainer.style.height = `${Math.round(rect.height)}px`;
      phaserContainer.style.zIndex = '20';

      // Clip hub background to only show OUTSIDE the game area (keep left side visible)
      if (hubBg) {
        hubBg.style.clipPath = `polygon(0 0, ${rect.left}px 0, ${rect.left}px 100%, 0 100%)`;
      }
    };

    requestAnimationFrame(sync);
    const ro = new ResizeObserver(sync);
    ro.observe(layoutRef.current);

    return () => {
      ro.disconnect();
      phaserContainer.style.cssText = savedPhaserCss;
      if (reactUi) reactUi.style.zIndex = savedReactUiZIndex;
      if (hubBg) hubBg.style.clipPath = savedHubBgClip;
      if (mainHub) mainHub.style.background = savedMainHubBg;
    };
  }, [isEmbedded]);

  // Redirect if projectId invalid, project not found, or contract not yet signed (route mode only)
  useEffect(() => {
    if (isEmbedded) return;
    if (!projectId || !project) {
      navigate('/hub', { replace: true });
    } else if (!project.contractResult) {
      navigate('/hub', { replace: true });
    }
  }, [projectId, project, navigate, isEmbedded]);

  // Initialize battle on mount
  useEffect(() => {
    if (initialized.current) return;
    if (!project?.contractResult) return;

    BattlePhase.initBattle(project.prepareSnapshot, project.contractResult);
    initialized.current = true;

    return () => {
      BattlePhase.cleanup();
      initialized.current = false;
    };
  }, [projectId]); // Depend on projectId (stable string), not project object

  // When battle ends, show result dialog instead of navigating immediately
  useEffect(() => {
    if (result === null || !projectId) return;

    // Collect stats for the dialog
    const store = useBattleStore.getState();
    setBattleResultData({
      outcome: result,
      turnsUsed: store.currentTurn,
      turnsTotal: store.maxTurns,
      totalDamage: store.stats.totalDamageDealt,
      maxCombo: store.stats.maxCombo,
    });
    setShowResultDialog(true);
  }, [result, projectId]);

  // Confirm result and navigate
  const handleConfirmResult = useCallback(() => {
    if (!projectId) return;
    const battleResult = BattlePhase.finalize();
    if (battleResult) {
      useProjectStore.getState().completeBattle(projectId, battleResult);
      if (isEmbedded && onComplete) {
        onComplete();
      } else {
        navigate('/hub');
      }
    }
  }, [projectId, navigate, isEmbedded, onComplete]);

  if (!projectId || !project || !project.contractResult) return null;

  const displayName = projectName ?? project.contractResult.project.name ?? 'PROJECT';

  return (
    <div ref={layoutRef} className="battle-layout">
      {/* Top status bar */}
      <div className="battle-layout__top">
        <ProjectStatus projectName={displayName} />
      </div>

      {/* Center: board area — only mount BoardView after battle is initialized
          so BattleScene.create() reads a populated boardStore */}
      <div className="battle-layout__center">
        {enemy !== null && <BoardView />}
      </div>

      {/* Bottom: agent team bar */}
      <div className="battle-layout__bottom">
        <AgentTeamBar />
      </div>

      {/* Overlay layer */}
      <div className="battle-layout__overlay">
        <ComboDisplay />
        <HardwareOverlay />
      </div>

      {/* Battle result dialog */}
      {showResultDialog && battleResultData && (
        <div className="battle-result-overlay">
          <div className="battle-result-dialog">
            <div
              className={`battle-result-dialog__title ${
                battleResultData.outcome === 'VICTORY'
                  ? 'battle-result-dialog__title--victory'
                  : 'battle-result-dialog__title--defeat'
              }`}
            >
              {battleResultData.outcome === 'VICTORY'
                ? '// DELIVERY COMPLETE'
                : '// DEADLINE EXCEEDED'}
            </div>

            <div className="battle-result-dialog__stats">
              <div className="battle-result-dialog__stat-row">
                <span className="battle-result-dialog__stat-label">TURNS</span>
                <span className="battle-result-dialog__stat-value">
                  {battleResultData.turnsUsed} / {battleResultData.turnsTotal}
                </span>
              </div>
              <div className="battle-result-dialog__stat-row">
                <span className="battle-result-dialog__stat-label">DAMAGE</span>
                <span className="battle-result-dialog__stat-value">
                  {battleResultData.totalDamage.toLocaleString()}
                </span>
              </div>
              <div className="battle-result-dialog__stat-row">
                <span className="battle-result-dialog__stat-label">MAX COMBO</span>
                <span className="battle-result-dialog__stat-value">
                  {battleResultData.maxCombo}x
                </span>
              </div>
            </div>

            <button
              className="battle-result-dialog__confirm"
              onClick={handleConfirmResult}
            >
              CONFIRM
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
